import React from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { makeStyles } from "@material-ui/styles";
import { Card, CardContent, Typography, Grid, Chip } from "@material-ui/core";
import { CrudOptions, TextClip } from "frame/components";
import { useRouter, isStaffUser, createRowSelector, useTerminology } from "utils";
import { CUSTOMERS_PATH } from "../actions/types";

const useStyles = makeStyles((theme) => ({
	card: {
		display: "flex",
		flexWrap: "wrap",
		position: "relative",
		marginBottom: theme.spacing(2),
	},
	container: {
		margin: theme.spacing(0.5),
	},
	content: {
		padding: theme.spacing(2),
		flexGrow: 1,
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		[theme.breakpoints.down("sm")]: {
			width: "100%",
			flexWrap: "wrap",
		},
		"&:last-child": {
			paddingBottom: theme.spacing(2),
		},
	},
	label: {
		padding: theme.spacing(0, 0, 0.5, 0),
		color: theme.palette.text.tertiary,
	},
	value: {
		textOverflow: "ellipsis",
		overflow: "hidden",
		padding: theme.spacing(0, 0, 0.5, 0),
	},
	valueInactive: {
		color: theme.palette.text.tertiary,
	},
	error: {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.white,
	},
	success: {
		backgroundColor: theme.palette.success.main,
		color: theme.palette.white,
	},
}));

const CustomerListing = ({
	role,
	customer,
	className,
	onEdit,
	onDelete,
	onSelect,
	...rest
}) => {
	const classes = useStyles();
	const { history } = useRouter();
	const { customerLabel } = useTerminology();
	const { id } = customer;

	const archived = String(customer.status).toLowerCase() === "inactive";
	const active = !archived;

	const onRowSelect = createRowSelector(() => {
		onSelect(id);
		history.push(`${CUSTOMERS_PATH}/${id}`);
	});

	const valueStyle = clsx(classes.value, archived ? classes.valueInactive : "");

	const deleteWarning = (
		<>
			Warning, deleting the customer &quot;{customer.customerName}&quot; will remove all
			associated projects.
			<br />
			This is permanent and cannot be undone.
		</>
	);

	const contactLines = [customer.contactPerson, customer.emailAddress].filter(Boolean);

	return (
		<Card
			{...rest}
			className={clsx(classes.card, className)}
			data-cy={`customer-${customer.customerName}`}
			onClick={onRowSelect}
			style={{ cursor: "pointer" }}
		>
			<CardContent className={classes.content}>
				<Grid container spacing={2} className={classes.container}>
					<Grid item lg={3} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							{customerLabel} Name
						</Typography>
						<Typography variant="h6" className={valueStyle}>
							<TextClip
								hideSeeMore
								text={customer.customerName || "-"}
								numberOfCharacters={60}
							/>
						</Typography>
					</Grid>
					<Grid item lg={5} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Contact Person
						</Typography>
						{contactLines.length > 0 ? (
							contactLines.map((line, i) => (
								<Typography key={i} variant="h6" className={valueStyle}>
									<TextClip hideSeeMore text={line} numberOfCharacters={60} />
								</Typography>
							))
						) : (
							<Typography variant="h6" className={valueStyle}>
								-
							</Typography>
						)}
					</Grid>
					<Grid item lg={2} md={6} xs={12}>
						<Typography className={classes.label} variant="body2">
							Status
						</Typography>
						<Chip
							className={active ? classes.success : classes.error}
							size="small"
							label={active ? "Active" : "Inactive"}
						/>
					</Grid>
				</Grid>
				{!isStaffUser(role) && (
					<div className="ignore">
						<CrudOptions
							onEdit={() => onEdit(id)}
							onDelete={() => onDelete(id)}
							customRemoveModelText={deleteWarning}
						/>
					</div>
				)}
			</CardContent>
		</Card>
	);
};

CustomerListing.propTypes = {
	role: PropTypes.string.isRequired,
	className: PropTypes.string,
	customer: PropTypes.object.isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onSelect: PropTypes.func.isRequired,
};

export default CustomerListing;
